import ChangeSessionActivityAppointment from 'components/SessionActivities/Modals/ChangeSessionActivityAppointment';
import { useAppointments } from 'src/composables/appointments';
import { notifyNegative, useDialog } from 'src/composables/interactions';
import { auth as useAuthStore } from 'src/store/auth';
import { logEvent } from 'src/utils/LogEvent';

// eslint-disable-next-line import/prefer-default-export
export function useStaffActivityAppointmentLink() {
  const authStore = useAuthStore();
  const { openComponentDialog } = useDialog();
  const {
    appointmentLookupStartDate, loadLinkableAppointments, linkAppointmentToSessionActivity,
  } = useAppointments();

  /**
   * A staff-only activity has no client - drive time, admin time and the like. Client, group and
   * supervision sessions all carry one, and are handled by the finish-session prompt instead.
   *
   * @param {Object} sessionActivity
   * @return {boolean}
   */
  function isStaffOnlyActivity(sessionActivity) {
    return !!sessionActivity && !sessionActivity.client;
  }

  /**
   * Whether the activity is a candidate for the link prompt, before looking for appointments to
   * offer. Kept free of reads so a clinic without an integration costs no Firestore queries.
   *
   * @param {Object} sessionActivity
   * @return {boolean}
   */
  function shouldPromptAppointmentLink(sessionActivity) {
    return !!authStore.connectedClinic?.integration
      && isStaffOnlyActivity(sessionActivity)
      && !sessionActivity.appointment;
  }

  /**
   * The staff-only appointments the activity could be linked to: same user, same day, no client.
   *
   * @param {Object} sessionActivity
   * @return {Promise<Object[]>}
   */
  function loadStaffLinkableAppointments(sessionActivity) {
    return loadLinkableAppointments({
      clinic: authStore.userDetails.clinic,
      integration: authStore.connectedClinic.integration,
      clientId: null,
      userId: sessionActivity.user,
      startDate: appointmentLookupStartDate(sessionActivity),
    });
  }

  /**
   * Resolves once the user has linked or skipped - with the link result, or null when skipped.
   *
   * A failed link resolves rather than rejects: the activity already exists at this point, so
   * blocking would leave the user unable to open it. They can still link mid-activity.
   *
   * @param {Object} sessionActivity
   * @param {Object[]} preloadedAppointments
   * @return {Promise<Object|null>}
   */
  function openAppointmentLinkModal(sessionActivity, preloadedAppointments) {
    return new Promise((resolve) => {
      openComponentDialog(
        ChangeSessionActivityAppointment,
        {
          appointmentId: '',
          clientId: null,
          userId: sessionActivity.user,
          startDate: appointmentLookupStartDate(sessionActivity),
          allowUnlink: false,
          linkPromptMode: true,
          preloadedAppointments,
        },
        async (selectedAppointmentId) => {
          if (!selectedAppointmentId) {
            resolve(null);
            return;
          }
          try {
            resolve(await linkAppointmentToSessionActivity(sessionActivity, selectedAppointmentId));
          } catch (err) {
            notifyNegative(err.message);
            logEvent(err);
            resolve(null);
          }
        },
        // No onDismiss handler: Quasar pushes one onto its OK handlers too, so it would resolve
        // this promise before the awaited link finishes. The dialog is persistent in prompt mode,
        // so Skip is the only other way out and onCancel already resolves for it.
        () => resolve(null),
      );
    });
  }

  /**
   * Offer to link a staff appointment to a freshly created staff-only activity. Does nothing at all
   * when there is no appointment to offer, so an activity without a scheduled counterpart - the
   * common case - is not interrupted.
   *
   * @param {Object} sessionActivity
   * @return {Promise<Object|null>} the link result, or null when nothing was linked
   */
  async function promptStaffActivityAppointmentLink(sessionActivity) {
    if (!shouldPromptAppointmentLink(sessionActivity)) {
      return null;
    }

    let appointments = [];
    try {
      appointments = await loadStaffLinkableAppointments(sessionActivity);
    } catch (err) {
      logEvent(err);
      return null;
    }

    if (!appointments.length) {
      return null;
    }

    return openAppointmentLinkModal(sessionActivity, appointments);
  }

  return {
    isStaffOnlyActivity,
    shouldPromptAppointmentLink,
    loadStaffLinkableAppointments,
    promptStaffActivityAppointmentLink,
  };
}
